import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClockAlert, CloudOff, Download, Hourglass, Plus, RefreshCw, TriangleAlert, Zap } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canAccessInvoicing as hasInvoicingAccess, canEdit as canEditModule } from '@/lib/permissions';
import { getErrorMessage } from '@/lib/errors';
import { formatMoney, formatNumber } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { StatCard } from '@/components/ui/stat-card';
import { notify } from '@/components/ui/notify';
import { ChipSelect, LinkButton, PhoneKpis, SearchField, Segmented, plural, useIsPhone } from '@/components/supply-finance/SfUi';
import {
  INVOICE_EXPORT_COLUMNS, MILESTONES, basisAmount, buildInvoiceRows, contractValue, dataCheck, filterReadyItems, filterRows,
  periodOptions, scopeProjects, summarizeRows, systemOf, viewBasis,
} from '@/components/supply-finance/invoicingModel';
import { buildRegisterRows, isDeletionRequested, isRegisterView, lastQuickBooksSync, registerCounts, unsyncedRows } from '@/components/supply-finance/registerModel';
import { isApprover } from '@/components/supply-finance/invoiceDeletionModel';
import { MilestoneListCard, MoneyBarCard, SystemTable } from '@/components/supply-finance/InvoicingParts';
import { InvoiceRegister } from '@/components/supply-finance/InvoiceRegister';
import { QuickBooksChip, SyncToQuickBooksButton, useQuickBooksStatus } from '@/components/supply-finance/QuickBooksControls';
import { SearchSelect } from '@/components/supply-finance/SearchSelect';
import { datedFilename, downloadCsv } from '@/components/supply-finance/csv';
import { RaiseInvoiceDialog, ReadyToBillCallout } from '@/components/supply-finance/ReadyToBill';
import { CollectionsForecastCard } from '@/components/supply-finance/CollectionsForecastCard';
import { useQbInvoiceMatches } from '@/components/supply-finance/QbDesktopMatch';

function InvoicingSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <div className="ui-skeleton-shimmer h-[150px] rounded-card" />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {['Awaiting payment', 'Older than 90 days', 'Ready to raise', 'Not in QuickBooks'].map((label) => <StatCard key={label} label={label} loading />)}
      </div>
    </div>
  );
}

const compactAed = (value) => {
  const number = Number(value) || 0;
  if (Math.abs(number) >= 1e6) return `${formatNumber(number / 1e6, { maxDecimals: 1 })}M`;
  if (Math.abs(number) >= 1e4) return `${formatNumber(number / 1e3, { decimals: 0 })}K`;
  return formatNumber(number, { decimals: 0 });
};

export default function InvoicingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const [invoices, setInvoices] = useState([]);
  const [variationInvoices, setVariationInvoices] = useState([]);
  const [voError, setVoError] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const canAccessInvoicing = hasInvoicingAccess(user);
  // View-only roles (Admin › Access) and Viewers see the register without the edit buttons; the server refuses too.
  const canEdit = canAccessInvoicing && canEditModule(user, 'invoicing');
  const quickBooks = useQuickBooksStatus(canAccessInvoicing);
  // QuickBooks Desktop, read only (owner decision 9 Oct 2026) — not the QuickBooks Online push above: each register
  // row's QuickBooks match, read once; null while it is off / not read yet / the request fails.
  const qbInvoiceMatches = useQbInvoiceMatches(canAccessInvoicing);
  const registerRef = useRef(null);

  const periods = useMemo(() => periodOptions(), []);
  const [periodKey, setPeriodKey] = useState('all');
  const [search, setSearch] = useState('');
  const [projectId, setProjectId] = useState(null);
  const [client, setClient] = useState(null);
  const [system, setSystem] = useState(null);
  const [milestone, setMilestone] = useState(null);
  // ?status=Submitted (Dashboard "Unpaid invoices") opens the page filtered to that invoice status.
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(() => String(searchParams.get('status') || '').trim() || null);
  // Register view + aging filter (the header's attention links drive them too). ?view=deletion&invoice=<id> (the bell's
  // "Deletion requested" notification, BUG-006) opens the deletion requests with that invoice highlighted.
  const linkedView = String(searchParams.get('view') || '').trim();
  const [registerView, setRegisterView] = useState(() => (isRegisterView(linkedView) ? linkedView : 'awaiting'));
  const highlightId = String(searchParams.get('invoice') || '').trim();
  const scrolledToLink = useRef(false);
  const [registerAging, setRegisterAging] = useState(null);
  // Wave 3: 24 #8 ready to bill, 24 #10 collections forecast (both computed on the server).
  const [ready, setReady] = useState(null);
  const [readyFailed, setReadyFailed] = useState(false);
  const [collections, setCollections] = useState({ data: null, loading: true, error: '' });
  const [forecastMonths, setForecastMonths] = useState(6);
  const [raising, setRaising] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Variation invoices are part of the register; if only they fail the page still shows the base invoices.
      const [invoiceRes, projectRes, voRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/projects'),
        api.get('/variation-invoices').catch((err) => ({ error: err })),
      ]);
      setInvoices(Array.isArray(invoiceRes.data) ? invoiceRes.data : []);
      setProjects(Array.isArray(projectRes.data) ? projectRes.data : []);
      if (voRes.error) {
        setVoError(getErrorMessage(voRes.error, 'Could not load the variation invoices'));
        setVariationInvoices([]);
      } else {
        setVoError('');
        setVariationInvoices(Array.isArray(voRes.data) ? voRes.data : []);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load invoices'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccessInvoicing) { setLoading(false); return; }
    load();
  }, [canAccessInvoicing, load]);

  const loadReady = useCallback(async () => {
    try {
      const res = await api.get('/invoicing/ready-to-bill');
      setReady(res.data || { items: [] });
      setReadyFailed(false);
    } catch {
      setReady(null); // the callout is an extra: without it the page works as before
      setReadyFailed(true);
    }
  }, []);
  const collectionsRequest = useRef(0);
  const loadCollections = useCallback(async () => {
    const requestId = collectionsRequest.current + 1;
    collectionsRequest.current = requestId;
    setCollections((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const params = { months: forecastMonths };
      if (projectId) params.project_id = projectId;
      if (client) params.client = client;
      if (system) params.system = system;
      if (milestone) params.milestone = milestone;
      const res = await api.get('/invoicing/collection-forecast', { params });
      if (collectionsRequest.current === requestId) setCollections({ data: res.data, loading: false, error: '' });
    } catch (err) {
      if (collectionsRequest.current === requestId) {
        setCollections({ data: null, loading: false, error: getErrorMessage(err, 'Could not load the collections forecast') });
      }
    }
  }, [forecastMonths, projectId, client, system, milestone]);
  useEffect(() => { if (canAccessInvoicing) loadReady(); }, [canAccessInvoicing, loadReady]);
  useEffect(() => { if (canAccessInvoicing) loadCollections(); }, [canAccessInvoicing, loadCollections]);
  const reloadAll = useCallback(async () => { await Promise.all([load(), loadReady(), loadCollections()]); }, [load, loadReady, loadCollections]);
  // Ready-to-bill milestones by invoice id (evidence, project status) — the register's "Ready to raise" view.
  const readyIds = useMemo(() => (ready ? new Map((ready.items || []).map((item) => [item.invoice_id, item])) : null), [ready]);

  const projectMap = useMemo(() => Object.fromEntries(projects.map((project) => [project.id, project])), [projects]);
  const rows = useMemo(() => buildInvoiceRows(invoices, projectMap), [invoices, projectMap]);
  const period = periods.find((item) => item.key === periodKey) || periods[periods.length - 1];
  const filteredRows = useMemo(
    () => filterRows(rows, { period, projectId, client, system, milestone, search, status }),
    [rows, period, projectId, client, system, milestone, search, status]
  );
  // The register: base + VO invoices under the same filters.
  const registerAll = useMemo(() => buildRegisterRows(invoices, variationInvoices, projectMap), [invoices, variationInvoices, projectMap]);
  const registerRows = useMemo(
    () => filterRows(registerAll, { period, projectId, client, system, milestone, search, status }),
    [registerAll, period, projectId, client, system, milestone, search, status]
  );
  const register = useMemo(() => registerCounts(registerRows, { readyIds }), [registerRows, readyIds]);
  // The callout and the Ready to raise tile follow the page filters (another client's money never shows under a filter).
  const readyInScope = useMemo(
    () => filterReadyItems(ready?.items, { projectId, client, system, milestone, status, search }, projectMap),
    [ready, projectId, client, system, milestone, status, search, projectMap]
  );
  const readyData = useMemo(
    () => ({ items: readyInScope, total: Math.round(readyInScope.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) * 100) / 100 }),
    [readyInScope]
  );
  const pendingSync = useMemo(() => unsyncedRows(registerRows), [registerRows]);
  // BUG-006: invoices someone asked to delete, waiting for an Admin (all of them, whatever the filters)
  const deletionRequests = useMemo(() => registerAll.filter(isDeletionRequested), [registerAll]);
  const approver = isApprover(user);
  const lastSync = useMemo(() => lastQuickBooksSync([...invoices, ...variationInvoices]), [invoices, variationInvoices]);

  const summary = useMemo(() => summarizeRows(filteredRows), [filteredRows]);
  const projectsInScope = useMemo(() => scopeProjects(projects, { projectId, client, system }), [projects, projectId, client, system]);
  const contract = useMemo(() => contractValue(projectsInScope), [projectsInScope]);

  const onlyScopeFilters = periodKey === 'all' && !milestone && !search.trim() && !status;
  const check = useMemo(() => {
    if (!onlyScopeFilters) return null;
    return dataCheck(filteredRows, projectsInScope);
  }, [onlyScopeFilters, filteredRows, projectsInScope]);

  const milestoneDef = milestone ? MILESTONES.find((item) => item.key === milestone) : null;
  const scopeLabel = projectId ? (projectMap[projectId]?.name || 'project') : client || system || 'all projects';
  // What the bar measures: the contract, a milestone's schedule, the search matches or a period's invoices.
  const basis = viewBasis({ period, milestone, search, status, scopeLabel });
  const base = basisAmount(basis.mode, { contract, summary });

  const systemItems = useMemo(() => {
    const types = [...new Set(projectsInScope.map(systemOf))];
    return types.map((systemType) => {
      const systemProjects = projectsInScope.filter((project) => systemOf(project) === systemType);
      const systemRows = filteredRows.filter((row) => row.systemType === systemType);
      const systemContract = contractValue(systemProjects);
      const systemSummary = summarizeRows(systemRows);
      return {
        systemType,
        projectCount: systemProjects.length,
        contract: systemContract,
        base: basisAmount(basis.mode, { contract: systemContract, summary: systemSummary }),
        summary: systemSummary,
      };
    })
      // The contract view lists every system with a contract value; a filtered view only the systems it touches.
      .filter((item) => (basis.mode === 'contract' ? item.contract > 0 || item.summary.scheduled > 0 : item.summary.scheduled > 0))
      .sort((a, b) => (b.summary.awaiting - a.summary.awaiting) || (b.base - a.base));
  }, [projectsInScope, filteredRows, basis.mode]);

  const projectOptions = useMemo(() => projects
    .filter((project) => rows.some((row) => row.projectId === project.id) || Number(project.total_project_value) > 0)
    .map((project) => ({ value: project.id, label: project.name || 'Unnamed project' }))
    .sort((a, b) => a.label.localeCompare(b.label)), [projects, rows]);
  const allProjectOptions = useMemo(() => projects
    .map((project) => ({ value: project.id, label: project.name || 'Unnamed project', sub: [project.client, project.system_type].filter(Boolean).join(' · ') }))
    .sort((a, b) => a.label.localeCompare(b.label)), [projects]);
  const clientOptions = useMemo(() => {
    const counts = new Map();
    projects.forEach((project) => { const name = String(project.client || '').trim(); if (name) counts.set(name, (counts.get(name) || 0) + 1); });
    return [...counts.entries()].map(([value, count]) => ({ value, label: value, count })).sort((a, b) => a.label.localeCompare(b.label));
  }, [projects]);
  const systemOptions = useMemo(() => {
    const counts = new Map();
    projects.forEach((project) => { const name = systemOf(project); counts.set(name, (counts.get(name) || 0) + 1); });
    return [...counts.entries()].map(([value, count]) => ({ value, label: value, count, muted: value === 'Unassigned' })).sort((a, b) => a.label.localeCompare(b.label));
  }, [projects]);
  const milestoneOptions = MILESTONES.map((item) => ({ value: item.key, label: item.label }));
  // Invoice statuses as stored (Not Started / Submitted / Payment Received …), with counts.
  const statusOptions = useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => counts.set(row.status, (counts.get(row.status) || 0) + 1));
    if (status && ![...counts.keys()].some((key) => key.toLowerCase() === status.toLowerCase())) counts.set(status, 0);
    return [...counts.entries()].map(([value, count]) => ({ value, label: value, count }));
  }, [rows, status]);
  const statusValue = statusOptions.find((option) => option.value.toLowerCase() === String(status || '').toLowerCase())?.value || status;

  const hasFilters = Boolean(projectId || client || system || milestone || status || search.trim() || periodKey !== 'all');
  const clearFilters = () => { setPeriodKey('all'); setSearch(''); setProjectId(null); setClient(null); setSystem(null); setMilestone(null); setStatus(null); };

  const exportRows = () => {
    downloadCsv(datedFilename('invoicing'), registerRows, INVOICE_EXPORT_COLUMNS);
    notify.success(`Exported ${plural(registerRows.length, 'invoice')}`, { description: 'Base and variation invoices · amounts in AED' });
  };
  const showRegister = (view, aging = null) => {
    setRegisterView(view);
    setRegisterAging(aging);
    setTimeout(() => registerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  };
  const showDeletionRequests = () => {
    // a filter could hide some of them: the link shows every request
    if (registerRows.filter(isDeletionRequested).length < deletionRequests.length) clearFilters();
    showRegister('deletion');
  };
  // A link to the register (?view=…) scrolls to it once the invoices are in.
  useEffect(() => {
    if (scrolledToLink.current || loading || !linkedView || !registerAll.length) return;
    scrolledToLink.current = true;
    setTimeout(() => registerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  }, [loading, linkedView, registerAll.length]);

  if (!canAccessInvoicing) {
    return (
      <div data-testid="invoicing-page">
        <EmptyState icon={CloudOff} title="You do not have access to invoicing" text="Ask an admin if you need to see billing and collections." />
      </div>
    );
  }

  const periodOn = basis.mode === 'period';
  const barSub = basis.mode === 'contract'
    ? plural(systemItems.length, 'system type')
    : plural(periodOn ? summary.invoicedCount : filteredRows.length, 'invoice');
  // The KPI is the base invoices (like the money bar); the register beside it adds the VO invoices.
  const baseOldest = registerRows
    .filter((row) => row.kind === 'base' && row.bucket === 'awaiting' && row.ageDays !== null)
    .reduce((best, row) => (!best || row.ageDays > best.ageDays ? row : best), null);
  const awaitingMeta = summary.awaitingCount
    ? `${plural(summary.awaitingCount, 'invoice')} · ${baseOldest ? `oldest ${formatNumber(baseOldest.ageDays)} d${baseOldest.projectName ? ` (${baseOldest.projectName})` : ''}` : plural(summary.awaitingProjects, 'project')}`
    : 'Nothing outstanding';
  // The register and the header count base + VO; the tile is the base invoices (like the money bar): say the rest.
  const voAwaiting = registerRows.filter((row) => row.kind === 'vo' && row.bucket === 'awaiting');
  const voAwaitingAmount = voAwaiting.reduce((sum, row) => sum + row.amount, 0);
  // Older than 90 days: who owes most of it ("4 at City Walk").
  const over90Rows = registerRows.filter((row) => row.bucket === 'awaiting' && row.aging === 'over90');
  const over90Top = (() => {
    const byProject = new Map();
    over90Rows.forEach((row) => {
      const key = row.projectName || 'Unknown project';
      const entry = byProject.get(key) || { name: key, count: 0, amount: 0 };
      entry.count += 1;
      entry.amount += row.amount;
      byProject.set(key, entry);
    });
    return [...byProject.values()].sort((a, b) => (b.count - a.count) || (b.amount - a.amount))[0] || null;
  })();
  const over90Meta = over90Rows.length
    ? `${plural(over90Rows.length, 'invoice')}${over90Top && over90Top.count > 1 ? ` · ${formatNumber(over90Top.count)} at ${over90Top.name} (${formatNumber(over90Top.amount, { decimals: 0 })})` : ''}`
    : 'Nothing waiting that long';
  const readyMeta = readyFailed
    ? 'Could not check the finished work'
    : readyInScope.length ? `${formatMoney(readyData.total, { decimals: 0 })} of finished work to bill` : 'Nothing finished waits to be billed';

  // One milestone picked: the bar above already is its split, so the milestone list is not shown (one column).
  const showMilestones = !milestoneDef && filteredRows.length > 0;
  const forecastScope = [projectId ? projectMap[projectId]?.name || 'project' : '', client || '', system || '', milestoneDef?.label || '']
    .filter(Boolean).join(' · ') || 'all projects';
  const forecastNote = [search.trim() && 'search', status && 'status', periodKey !== 'all' && 'period'].filter(Boolean);
  const forecastNoteText = forecastNote.length ? `${forecastNote.join(' and ').replace(/ and (?=.* and )/, ', ')} not applied` : '';

  const header = (
    <PageHeader
      title="Invoicing"
      subtitle="Billing and collections across all projects · base + variation invoices"
      alerts={[
        register.awaitingCount > 0 && { key: 'awaiting', label: `${formatNumber(register.awaitingCount)} awaiting payment`, tone: 'amber', onClick: () => showRegister('awaiting') },
        register.over90 > 0 && { key: 'over90', label: `${formatNumber(register.over90)} older than 90 days`, tone: 'red', onClick: () => showRegister('awaiting', 'over90') },
        // Admins decide deletion requests (BUG-006): say how many wait for them.
        approver && canEdit && deletionRequests.length > 0 && {
          key: 'deletion', label: `${plural(deletionRequests.length, 'deletion request')} to decide`, tone: 'red', onClick: showDeletionRequests,
        },
      ]}
      actions={(
        <>
          <QuickBooksChip status={quickBooks} lastSync={lastSync} />
          <Button variant="ghost" onClick={exportRows} disabled={!registerRows.length} className="px-2.5 text-ink-2 sm:px-3.5" aria-label="Export CSV" data-testid="invoicing-export">
            <Download aria-hidden="true" /><span className="hidden sm:inline">Export</span>
          </Button>
          {canEdit ? <SyncToQuickBooksButton status={quickBooks} pending={pendingSync} onSynced={load} /> : null}
          {canEdit ? (
            <SearchSelect
              value=""
              onChange={(id) => navigate(`/projects/${id}?tab=invoicing`)}
              options={allProjectOptions}
              searchPlaceholder="Which project is the invoice for?"
              emptyText="No project matches"
              align="end"
              popoverClassName="w-[320px]"
              trigger={(
                <Button data-testid="invoicing-new-invoice">
                  <Plus aria-hidden="true" /> New invoice
                </Button>
              )}
            />
          ) : null}
        </>
      )}
    />
  );

  const filterRow = (
    <div className="flex flex-wrap items-center gap-2" data-testid="invoicing-filters">
      <Segmented
        ariaLabel="Period"
        value={periodKey}
        onChange={setPeriodKey}
        testId="invoicing-period"
        options={periods.map((item) => ({ key: item.key, label: item.label, testId: `invoicing-period-${item.key}`, title: item.from ? `Recorded ${item.from} – ${item.to}` : 'Every invoice and milestone' }))}
      />
      <SearchField value={search} onChange={setSearch} placeholder="Invoice, project, client" label="Search invoice no., project or client" shortcut className="min-w-0 flex-1 basis-[200px] sm:w-[236px] sm:flex-none" testId="invoicing-search" />
      <ChipSelect label="Project" value={projectId} options={projectOptions} onChange={setProjectId} testId="invoicing-filter-project" />
      <ChipSelect label="Client" value={client} options={clientOptions} onChange={setClient} testId="invoicing-filter-client" emptyText="No clients recorded on projects" />
      <ChipSelect label="System" value={system} options={systemOptions} onChange={setSystem} testId="invoicing-filter-system" />
      <ChipSelect label="Milestone" value={milestone} options={milestoneOptions} onChange={setMilestone} testId="invoicing-filter-milestone" />
      <ChipSelect label="Status" value={statusValue} options={statusOptions} onChange={setStatus} testId="invoicing-filter-status" />
      {hasFilters ? <LinkButton onClick={clearFilters} className="px-1">Clear filters</LinkButton> : null}
    </div>
  );

  const kpiStrip = isPhone ? (
    <PhoneKpis
      testId="invoicing-kpis"
      items={[
        { key: 'awaiting', prefix: 'AED', value: compactAed(summary.awaiting), label: 'awaiting payment', tone: summary.awaiting > 0 ? 'amber' : null },
        { key: 'over90', prefix: 'AED', value: compactAed(register.aging.over90.amount), label: 'older than 90 d', tone: over90Rows.length ? 'red' : null, onClick: over90Rows.length ? () => showRegister('awaiting', 'over90') : undefined },
        { key: 'ready', value: readyFailed ? '—' : formatNumber(readyInScope.length), label: readyInScope.length === 1 ? 'ready to raise' : 'ready to raise', tone: readyInScope.length ? 'lime' : null, onClick: readyInScope.length ? () => showRegister('ready') : undefined },
      ]}
    />
  ) : (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="invoicing-kpis">
      <StatCard
        label="Awaiting payment"
        icon={Hourglass}
        tone="amber"
        prefix="AED"
        value={formatNumber(summary.awaiting, { decimals: 0 })}
        valueTone={summary.awaiting > 0 ? 'amber' : undefined}
        meta={awaitingMeta}
        onClick={summary.awaitingCount ? () => showRegister('awaiting') : undefined}
        data-testid="kpi-awaiting"
      >
        {voAwaiting.length ? (
          <div className="truncate text-[12px] text-ink-3" data-testid="kpi-awaiting-vo">
            + {plural(voAwaiting.length, 'VO invoice')} · AED {formatNumber(voAwaitingAmount, { decimals: 0 })}
          </div>
        ) : null}
      </StatCard>
      <StatCard
        label="Older than 90 days"
        icon={ClockAlert}
        tone={over90Rows.length ? 'red' : 'gray'}
        prefix="AED"
        value={formatNumber(register.aging.over90.amount, { decimals: 0 })}
        valueTone={over90Rows.length ? 'red' : undefined}
        meta={over90Meta}
        onClick={over90Rows.length ? () => showRegister('awaiting', 'over90') : undefined}
        data-testid="kpi-over-90"
      />
      <StatCard
        label="Ready to raise"
        icon={Zap}
        tone="lime"
        value={readyFailed ? '—' : formatNumber(readyInScope.length)}
        unit={readyFailed ? undefined : readyInScope.length === 1 ? 'milestone' : 'milestones'}
        meta={readyMeta}
        loading={ready === null && !readyFailed}
        onClick={readyInScope.length ? () => showRegister('ready') : undefined}
        data-testid="kpi-ready"
      />
      <StatCard
        label="Not in QuickBooks"
        icon={RefreshCw}
        tone="blue"
        value={formatNumber(pendingSync.length)}
        meta="raised invoices without a QB number"
        data-testid="kpi-not-in-qb"
      />
    </div>
  );

  return (
    <div data-testid="invoicing-page" className="space-y-4">
      {header}
      {error && !invoices.length ? (
        <EmptyState layout="inline" tone="red" icon={CloudOff} title="Couldn't load invoices" text={error}
          action={<Button variant="outline" size="sm" onClick={load}>Try again</Button>} />
      ) : loading && !invoices.length ? (
        <InvoicingSkeleton />
      ) : (
        <>
          {filterRow}
          <MoneyBarCard
            summary={summary}
            base={base}
            label={basis.label}
            sub={barSub}
            restLabel={basis.restLabel}
            ofBase={basis.ofBase}
            showOfInvoiced={!periodOn}
            check={check}
          />
          {kpiStrip}
          <ReadyToBillCallout data={readyData} canEdit={canEdit} onRaised={reloadAll} onViewAll={() => showRegister('ready')} />
          {voError ? (
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl bg-status-amber-bg px-3.5 py-2 text-[12.5px] text-status-amber-ink" role="alert" data-testid="invoicing-vo-error">
              <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
              Variation invoices did not load — the register shows base invoices only.
              <LinkButton onClick={load}>Try again</LinkButton>
            </p>
          ) : null}
          {registerAll.length ? (
            <>
              <div ref={registerRef} className={cn('grid scroll-mt-24 grid-cols-1 items-start gap-4', showMilestones && 'xl:grid-cols-[minmax(0,1fr)_380px]')}>
                <InvoiceRegister
                  rows={registerRows}
                  onChanged={reloadAll}
                  readyIds={readyIds}
                  onRaise={canEdit ? (row) => setRaising({ ...row, project_name: row.projectName }) : undefined}
                  canEdit={canEdit}
                  filtered={hasFilters}
                  onClearFilters={clearFilters}
                  view={registerView}
                  onViewChange={setRegisterView}
                  aging={registerAging}
                  onAgingChange={setRegisterAging}
                  highlightId={highlightId}
                  qbMatches={qbInvoiceMatches}
                />
                {/* One milestone picked: the bar above already is its split, so the list is not shown. */}
                {!showMilestones ? null : (
                  <MilestoneListCard summary={summary} rows={filteredRows} sub={periodOn ? `${scopeLabel} · recorded in ${period.label}` : scopeLabel} />
                )}
              </div>
              <CollectionsForecastCard
                data={collections.data}
                loading={collections.loading}
                error={collections.error}
                onRetry={loadCollections}
                months={forecastMonths}
                onMonthsChange={setForecastMonths}
                scopeLabel={forecastScope}
                scopeNote={forecastNoteText}
              />
              {filteredRows.length ? <SystemTable items={systemItems} basis={basis} /> : null}
            </>
          ) : (
            <EmptyState title="No invoices yet" text="Invoices and payment milestones are added on each project’s Invoicing tab." />
          )}
        </>
      )}
      <RaiseInvoiceDialog item={raising} open={Boolean(raising)} onOpenChange={(open) => { if (!open) setRaising(null); }} onRaised={reloadAll} />
    </div>
  );
}

